import React, {
	useState,
	useEffect,
	useReducer,
	useCallback,
	useContext,
	useRef,
	useMemo,
} from "react";
import { toast } from "react-toastify";

import openSocket from "../../services/socket-io";
import AppPageTitle from "../../components/AppPageTitle";
import AppTableActionMenu from "../../components/AppTableActionMenu";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import PermissionModal from "../../components/PermissionModal";
import { AuthContext } from "../../context/Auth/AuthContext";

function IconSearch() {
	return (
		<svg className="size-3.5 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconPlus({ className = "size-4 me-1 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</svg>
	);
}

const PAGE_SIZE = 20;

function IconChevronLeft({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="15 18 9 12 15 6" />
		</svg>
	);
}

function IconChevronRight({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="9 18 15 12 9 6" />
		</svg>
	);
}

function IconTrash() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function IconMore() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="1" />
			<circle cx="19" cy="12" r="1" />
			<circle cx="5" cy="12" r="1" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg className="size-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
			<path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
		</svg>
	);
}

function IconDelete() {
	return (
		<svg className="size-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function TagRowMenu({ onEdit, onDelete }) {
	return (
		<AppTableActionMenu
			triggerIcon={<IconMore />}
			triggerAriaLabel={i18n.t("tags.table.actions")}
			minWidth={144}
			items={[
				{
					key: "edit",
					label: i18n.t("tags.buttons.edit"),
					icon: <IconEdit />,
					onClick: onEdit,
				},
				{
					key: "delete",
					label: i18n.t("tags.buttons.delete"),
					icon: <IconDelete />,
					tone: "danger",
					onClick: onDelete,
				},
			]}
		/>
	);
}

function TagColorBadge({ color }) {
	const hex = color || "#71717a";
	return (
		<span
			className="inline-flex items-center gap-x-1.5 rounded px-2.5 py-0.5 text-xs font-medium"
			style={{
				backgroundColor: `${hex}1a`,
				color: hex,
				border: `1px solid ${hex}40`,
			}}
			title={hex}
		>
			<span
				className="size-2.5 shrink-0 rounded-full"
				style={{ backgroundColor: hex }}
				aria-hidden
			/>
			{hex.toUpperCase()}
		</span>
	);
}

function TagInfoItem({ label, value, children }) {
	return (
		<div>
			<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
				{label}
			</p>
			{children || (
				<p className="m-0 text-sm text-default-700 dark:text-default-200">{value}</p>
			)}
		</div>
	);
}

function TagsTableSkeleton() {
	return (
		<>
			{[1, 2, 3, 4, 5, 6].map((row) => (
				<tr key={row} className="animate-pulse">
					<td className="py-3 ps-4">
						<div className="size-4 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="h-4 w-28 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="h-5 w-20 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="h-4 w-10 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="h-5 w-16 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="ms-auto size-7.5 rounded bg-default-200 dark:bg-default-700" />
					</td>
				</tr>
			))}
		</>
	);
}

const reducer = (state, action) => {
	if (action.type === "SET_TAGS") {
		return action.payload;
	}

	if (action.type === "UPDATE_TAGS") {
		const tag = action.payload;
		const tagIndex = state.findIndex((s) => s.id === tag.id);

		if (tagIndex !== -1) {
			state[tagIndex] = tag;
			return [...state];
		}
		return [tag, ...state];
	}

	if (action.type === "DELETE_TAGS") {
		const tagId = action.payload;

		const tagIndex = state.findIndex((s) => s.id === tagId);
		if (tagIndex !== -1) {
			state.splice(tagIndex, 1);
		}
		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}

	return state;
};

function Tags() {
	const [loading, setLoading] = useState(false);
	const [pageNumber, setPageNumber] = useState(1);
	const [total, setTotal] = useState(0);
	const [selectedTag, setSelectedTag] = useState(null);
	const [deletingTag, setDeletingTag] = useState(null);
	const [deletingSelectedIds, setDeletingSelectedIds] = useState(null);
	const [selectedIds, setSelectedIds] = useState(() => new Set());
	const [permissionOpen, setPermissionOpen] = useState(false);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [tags, dispatch] = useReducer(reducer, []);
	const [tagModalOpen, setTagModalOpen] = useState(false);
	const { user } = useContext(AuthContext);

	const fetchTags = useCallback(async () => {
		try {
			const { data } = await api.get("/tags/", {
				params: { searchParam, pageNumber },
			});
			dispatch({ type: "SET_TAGS", payload: data.tags || [] });
			setTotal(data.count ?? 0);
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	}, [searchParam, pageNumber]);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
		setSelectedIds(new Set());
	}, [searchParam]);

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const rangeStart = total ? (pageNumber - 1) * PAGE_SIZE + 1 : 0;
	const rangeEnd = Math.min(pageNumber * PAGE_SIZE, total);

	const pageNumbers = () => {
		const pages = [];
		const max = 5;
		let from = Math.max(1, pageNumber - 2);
		let to = Math.min(totalPages, from + max - 1);
		from = Math.max(1, to - max + 1);
		for (let p = from; p <= to; p += 1) pages.push(p);
		return pages;
	};

	const visibleIds = useMemo(() => tags.map((t) => t.id), [tags]);
	const allVisibleSelected =
		visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
	const someVisibleSelected =
		visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;
	const selectedCount = selectedIds.size;

	const selectAllRef = useRef(null);
	useEffect(() => {
		if (!selectAllRef.current) return;
		selectAllRef.current.indeterminate = someVisibleSelected;
		selectAllRef.current.checked = allVisibleSelected;
	}, [someVisibleSelected, allVisibleSelected]);

	const toggleSelectAll = () => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (allVisibleSelected) {
				visibleIds.forEach((id) => next.delete(id));
			} else {
				visibleIds.forEach((id) => next.add(id));
			}
			return next;
		});
	};

	const toggleSelectOne = (id) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	useEffect(() => {
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			fetchTags();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, pageNumber, fetchTags]);

	useEffect(() => {
		const socket = openSocket({
			scope: "tags",
			userId: user.id,
			component: "Tags",
			companyId: user.companyId,
		});

		socket.on("tags", (data) => {
			if (data.action === "update" || data.action === "create") {
				if (data.tag && (data.tag.companyId == null || data.tag.companyId === user.companyId)) {
					dispatch({ type: "UPDATE_TAGS", payload: data.tag });
				}
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user.id, user.companyId]);

	const handleOpenTagModal = () => {
		setSelectedTag(null);
		setTagModalOpen(true);
	};

	const handleCloseTagModal = () => {
		setSelectedTag(null);
		setTagModalOpen(false);
	};

	const handleSearch = (event) => {
		setSearchParam(event.target.value.toLowerCase());
	};

	const handleEditTag = (tag) => {
		setSelectedTag(tag);
		setTagModalOpen(true);
	};

	const handleDeleteTag = async (tagId) => {
		try {
			await api.delete(`/tags/${tagId}`);
			toast.success(i18n.t("tags.toasts.deleted"));
		} catch (err) {
			toastError(err);
		}
		setDeletingTag(null);
		setSelectedIds((prev) => {
			const next = new Set(prev);
			next.delete(tagId);
			return next;
		});
		if (tags.length === 1 && pageNumber > 1) {
			setPageNumber((p) => p - 1);
		} else {
			await fetchTags();
		}
	};

	const handleDeleteSelected = async (ids) => {
		try {
			await Promise.all(ids.map((id) => api.delete(`/tags/${id}`)));
			toast.success(i18n.t("tags.toasts.deletedSelected"));
		} catch (err) {
			toastError(err);
		}
		setDeletingSelectedIds(null);
		setSelectedIds(new Set());
		if (tags.length <= ids.length && pageNumber > 1) {
			setPageNumber((p) => p - 1);
		} else {
			await fetchTags();
		}
	};

	const confirmDeleteSelected = () => {
		if (selectedCount === 0) return;
		if (user.profile !== "admin") {
			setPermissionOpen(true);
			return;
		}
		setDeletingTag(null);
		setDeletingSelectedIds([...selectedIds]);
		setConfirmModalOpen(true);
	};

	const confirmDeleteOne = (tag) => {
		if (user.profile === "admin") {
			setDeletingTag(tag);
			setDeletingSelectedIds(null);
			setConfirmModalOpen(true);
			return;
		}
		setPermissionOpen(true);
	};

	return (
		<div className="app-admin-page">
			<PermissionModal open={permissionOpen} onClose={setPermissionOpen} />
			<ConfirmationModal
				title={
					deletingTag
						? i18n.t("tags.confirmationModal.deleteTitle")
						: i18n.t("tags.confirmationModal.deleteSelectedTitle")
				}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={() =>
					deletingTag
						? handleDeleteTag(deletingTag.id)
						: handleDeleteSelected(deletingSelectedIds)
				}
			>
				{deletingTag
					? i18n.t("tags.confirmationModal.deleteMessage")
					: i18n.t("tags.confirmationModal.deleteSelectedMessage", {
							count: deletingSelectedIds?.length ?? 0,
					  })}
			</ConfirmationModal>
			<TagModal
				open={tagModalOpen}
				onClose={handleCloseTagModal}
				reload={fetchTags}
				tagId={selectedTag && selectedTag.id}
			/>

			<div className="app-admin-page__scroll">
				<AppPageTitle
					title={i18n.t("tags.title")}
					subtitle={i18n.t("tags.subtitle")}
				/>

				<div className="card">
					<div className="card-header flex-wrap items-start justify-between gap-3 px-5 pt-5 pb-4">
						<div>
							<h6 className="card-title m-0">{i18n.t("tags.listTitle")}</h6>
						</div>
						<button
							type="button"
							className="btn btn-sm shrink-0 bg-primary text-white"
							onClick={handleOpenTagModal}
						>
							<IconPlus />
							{i18n.t("tags.buttons.add")}
						</button>
					</div>

					<div className="card-header flex-wrap gap-3 px-5 py-4">
						<div className="flex w-full min-w-0 flex-1 flex-col gap-3 md:flex-row md:items-center md:space-y-0">
							<div className="relative w-full md:max-w-sm">
								<input
									type="search"
									className="form-input form-input-sm w-full ps-9"
									placeholder={i18n.t("tags.searchPlaceholder")}
									value={searchParam}
									onChange={handleSearch}
								/>
								<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
									<IconSearch />
								</div>
							</div>
						</div>

						{selectedCount > 0 && (
							<div className="flex flex-wrap items-center gap-2">
								<span className="text-sm text-default-500 dark:text-default-400">
									{i18n.t("tags.selectedCount", { count: selectedCount })}
								</span>
								<button
									type="button"
									className="btn btn-sm bg-danger/10 text-danger hover:bg-danger/20 dark:bg-danger/15 dark:hover:bg-danger/25"
									onClick={confirmDeleteSelected}
								>
									<IconTrash />
									<span className="ms-1">{i18n.t("tags.buttons.deleteSelected")}</span>
								</button>
							</div>
						)}
					</div>

					<div className="flex min-h-0 flex-1 flex-col">
						{loading && tags.length === 0 ? (
							<div className="overflow-x-auto px-2 pt-2">
								<table className="min-w-full">
									<tbody>
										<TagsTableSkeleton />
									</tbody>
								</table>
							</div>
						) : !loading && tags.length === 0 ? (
							<div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
								<p className="m-0 font-semibold text-default-800 dark:text-default-100">
									{i18n.t("tags.empty")}
								</p>
								{searchParam && (
									<p className="m-0 text-sm text-default-500 dark:text-default-400">
										{i18n.t("tags.emptyHint")}
									</p>
								)}
							</div>
						) : (
							<div className="flex min-h-0 flex-1 flex-col">
								<div className="space-y-3 px-4 pb-4 md:hidden">
									{tags.map((tag) => (
										<div
											key={tag.id}
											className="rounded-2xl border border-default-200 bg-white p-4 shadow-sm dark:border-default-700 dark:bg-default-950"
										>
											<div className="mb-4 flex items-start justify-between gap-3">
												<div className="flex min-w-0 items-start gap-3">
													<input
														type="checkbox"
														className="form-checkbox mt-0.5 shrink-0"
														checked={selectedIds.has(tag.id)}
														onChange={() => toggleSelectOne(tag.id)}
														aria-label={i18n.t("tags.selectRow", {
															name: tag.name,
														})}
													/>
													<div className="min-w-0">
														<p className="m-0 truncate text-sm font-semibold text-default-800 dark:text-default-100">
															{tag.name}
														</p>
													</div>
												</div>
												<TagRowMenu
													onEdit={() => handleEditTag(tag)}
													onDelete={() => confirmDeleteOne(tag)}
												/>
											</div>

											<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
												<TagInfoItem label={i18n.t("tags.table.color")}>
													<TagColorBadge color={tag.color} />
												</TagInfoItem>
												<TagInfoItem
													label={i18n.t("tags.table.contacts")}
													value={tag.contacttag?.length ?? 0}
												/>
												<TagInfoItem label={i18n.t("tags.table.absentTag")}>
													{tag.tagAusente ? (
														<span className="inline-flex items-center gap-x-1 rounded px-2.5 py-0.5 text-xs font-medium bg-warning/10 text-warning">
															{i18n.t("tags.table.absentTagYes")}
														</span>
													) : (
														<span className="inline-flex items-center gap-x-1 rounded px-2.5 py-0.5 text-xs font-medium bg-default-200 text-default-600 dark:bg-default-700 dark:text-default-400">
															—
														</span>
													)}
												</TagInfoItem>
											</div>
										</div>
									))}
								</div>

								<div className="hidden md:block">
									<div className="app-table-scroll">
										<div className="inline-block min-w-full align-middle">
											<div className="overflow-hidden">
												<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
													<thead className="bg-default-150 dark:bg-default-800/60">
														<tr className="whitespace-nowrap text-sm font-normal text-default-700 dark:text-default-300">
															<th scope="col" className="ps-4 text-start">
																<input
																	ref={selectAllRef}
																	type="checkbox"
																	className="form-checkbox"
																	checked={allVisibleSelected}
																	onChange={toggleSelectAll}
																	aria-label={i18n.t("tags.selectAll")}
																	disabled={visibleIds.length === 0 || loading}
																/>
															</th>
															<th scope="col" className="px-3.5 py-3 text-start">
																{i18n.t("tags.table.name")}
															</th>
															<th scope="col" className="px-3.5 py-3 text-start">
																{i18n.t("tags.table.color")}
															</th>
															<th scope="col" className="px-3.5 py-3 text-start">
																{i18n.t("tags.table.contacts")}
															</th>
															<th scope="col" className="px-3.5 py-3 text-start">
																{i18n.t("tags.table.absentTag")}
															</th>
															<th scope="col" className="px-3.5 py-3 text-start">
																{i18n.t("tags.table.actions")}
															</th>
														</tr>
													</thead>
													<tbody className="divide-y divide-default-200 dark:divide-default-700">
														{tags.map((tag) => (
															<tr
																key={tag.id}
																className="whitespace-nowrap text-sm font-normal text-default-800 dark:text-default-200"
															>
																<td className="py-3 ps-4">
																	<input
																		type="checkbox"
																		className="form-checkbox"
																		checked={selectedIds.has(tag.id)}
																		onChange={() => toggleSelectOne(tag.id)}
																		aria-label={i18n.t("tags.selectRow", {
																			name: tag.name,
																		})}
																	/>
																</td>
																<td className="px-3.5 py-3">
																	<h6 className="mb-0 font-semibold text-default-800 dark:text-default-100">
																		{tag.name}
																	</h6>
																</td>
																<td className="px-3.5 py-3">
																	<TagColorBadge color={tag.color} />
																</td>
																<td className="px-3.5 py-3 text-default-800 dark:text-default-200">
																	{tag.contacttag?.length ?? 0}
																</td>
																<td className="px-3.5 py-3">
																	{tag.tagAusente ? (
																		<span className="inline-flex items-center gap-x-1 rounded px-2.5 py-0.5 text-xs font-medium bg-warning/10 text-warning">
																			{i18n.t("tags.table.absentTagYes")}
																		</span>
																	) : (
																		<span className="inline-flex items-center gap-x-1 rounded px-2.5 py-0.5 text-xs font-medium bg-default-200 text-default-600 dark:bg-default-700 dark:text-default-400">
																			—
																		</span>
																	)}
																</td>
																<td className="px-3.5 py-3">
																	<TagRowMenu
																		onEdit={() => handleEditTag(tag)}
																		onDelete={() => confirmDeleteOne(tag)}
																	/>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										</div>
									</div>
								</div>

								{total > 0 && (
									<div className="card-footer">
										<p className="m-0 text-sm text-default-500">
											{i18n.t("tags.pagination.showing", {
												start: rangeStart,
												end: rangeEnd,
												total,
											})}
										</p>
										<nav
											className="flex flex-wrap items-center gap-2"
											aria-label={i18n.t("tags.pagination.label")}
										>
											<button
												type="button"
												className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
												disabled={pageNumber <= 1 || loading}
												onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
											>
												<IconChevronLeft className="size-4 me-1 shrink-0" />
												{i18n.t("tags.pagination.previous")}
											</button>
											{pageNumbers().map((p) => (
												<button
													key={p}
													type="button"
													className={`btn size-7.5 shrink-0 border ${
														p === pageNumber
															? "border-primary bg-primary text-white"
															: "border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
													}`}
													disabled={loading}
													onClick={() => setPageNumber(p)}
												>
													{p}
												</button>
											))}
											<button
												type="button"
												className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
												disabled={pageNumber >= totalPages || loading}
												onClick={() => setPageNumber((p) => p + 1)}
											>
												{i18n.t("tags.pagination.next")}
												<IconChevronRight className="size-4 ms-1 shrink-0" />
											</button>
										</nav>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default Tags;
